import React, {useState, useMemo, useEffect} from 'react';
import {
	Card,
	CardHeader,
	CardContent,
	Table,
	TableBody,
	TableRow,
	TableCell,
	Typography,
	Chip,
	Box,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
	Collapse,
	TextField,
	makeStyles,
} from '@material-ui/core';
import LaunchIcon from '@material-ui/icons/Launch';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import AcUnitIcon from '@material-ui/icons/AcUnit';
import moment from 'moment';
import SearchableSelect from 'react-select';

import PickupPointMap from './PickupPointMap';
import colors from '../../../../theme/colors';
import API from '../../../../network/dashboardAPI';
import api from '../../../../network/api';
import useAlert from '../../../../services/useAlert';

const useStyles = makeStyles((theme) => ({
	table: {
		borderCollapse: 'separate',
		borderSpacing: 0,
		'& td, & th': {
			borderBottom: 'none',
			padding: '8px 12px',
			verticalAlign: 'top',
		},
		'& .MuiTableRow-root': {
			'&:hover': {
				backgroundColor: theme.palette.action.hover,
			},
		},
	},
	labelCell: {
		fontWeight: 'bold',
		width: '30%',
		whiteSpace: 'nowrap',
	},
	link: {
		display: 'inline-flex',
		alignItems: 'center',
		color: colors.blue,
		textDecoration: 'none',
		marginLeft: theme.spacing(1),
		cursor: 'pointer',
	},
	chip: {
		marginRight: theme.spacing(1),
		height: 22,
		fontSize: '0.72rem',
	},
	hoursRow: {
		display: 'flex',
		justifyContent: 'space-between',
		padding: '2px 0',
		fontSize: '0.82rem',
	},
	hoursToday: {
		fontWeight: 'bold',
		color: colors.darkBold,
	},
	hoursToggle: {
		textTransform: 'none',
		padding: 0,
		minWidth: 0,
		fontSize: '0.75rem',
	},
	mapWrapper: {
		marginTop: theme.spacing(2),
	},
	noCoords: {
		marginTop: theme.spacing(2),
		padding: theme.spacing(2),
		backgroundColor: colors.lightMediumColor,
		borderRadius: 4,
		textAlign: 'center',
	},
}));

const hasCoordinates = (point) =>
	point &&
	point.latitude !== null &&
	point.latitude !== undefined &&
	point.longitude !== null &&
	point.longitude !== undefined &&
	!Number.isNaN(Number(point.latitude)) &&
	!Number.isNaN(Number(point.longitude));

const PickupPointCard = ({details, onUpdated, hasUnsavedChanges}) => {
	const classes = useStyles();
	const alert = useAlert();
	const [showAllHours, setShowAllHours] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [points, setPoints] = useState([]);
	const [selectedId, setSelectedId] = useState('');
	const [loadingPoints, setLoadingPoints] = useState(false);
	const [saving, setSaving] = useState(false);
	const [dialogError, setDialogError] = useState('');
	const [contactPhone, setContactPhone] = useState(details?.pickup_contact_phone || '');
	const [savingPhone, setSavingPhone] = useState(false);
	const [phoneError, setPhoneError] = useState('');

	useEffect(() => {
		setContactPhone(details?.pickup_contact_phone || '');
		setPhoneError('');
	}, [details?.pickup_contact_phone]);

	const pickupPoint = details?.pickup_point;
	const deliveryCompany = pickupPoint?.delivery_company;
	const isPickupOrder = Number(details?.self_deliver) === 1;
	const saveContactPhone = async () => {
		const phone = contactPhone.trim();
		if (!phone) {
			setPhoneError('Enter a contact phone number');
			return;
		}
		setSavingPhone(true);
		setPhoneError('');
		try {
			const response = await api.request(`api/orders/${details.order_id}/pickup-contact-phone`, 'PUT', {pickup_contact_phone: phone});
			const result = await response.json();
			if (!result.success) throw new Error(result.message || result.data || 'Could not save contact phone number');
			await onUpdated();
			alert.toast('Pickup contact phone number saved', 'success');
		} catch (error) {
			setPhoneError(error.message || 'Could not save contact phone number');
		} finally {
			setSavingPhone(false);
		}
	};
	const pointOptions = useMemo(() => {
		const options = points.map((point) => ({
			value: point.id,
			label: `${point.name}${point.address ? ` — ${point.address}` : ''}`,
		}));
		if (pickupPoint && !points.some((point) => Number(point.id) === Number(pickupPoint.id))) {
			options.unshift({value: pickupPoint.id, label: `${pickupPoint.name} (inactive)`, isDisabled: true});
		}
		return options;
	}, [points, pickupPoint]);

	const openDialog = async () => {
		setSelectedId(pickupPoint?.id || '');
		setDialogError('');
		setDialogOpen(true);
		setLoadingPoints(true);
		try {
			const response = await API.getPickupPointsList({is_active: true, page: 1, limit: 500});
			const result = await response.json();
			if (!result.success) throw new Error(result.data || 'Could not load pickup points');
			setPoints(result.data?.items || []);
		} catch (error) {
			setDialogError(error.message || 'Could not load pickup points');
		} finally {
			setLoadingPoints(false);
		}
	};

	const savePoint = async () => {
		if (!selectedId || Number(selectedId) === Number(pickupPoint?.id)) {
			setDialogOpen(false);
			return;
		}
		setSaving(true);
		setDialogError('');
		try {
			const response = await API.updateOrderPickupPoint(details.order_id, Number(selectedId));
			const result = await response.json();
			if (!result.success) throw new Error(result.message || result.data || 'Could not update pickup point');
			await onUpdated();
			setDialogOpen(false);
			alert.toast('Pickup point updated', 'success');
		} catch (error) {
			setDialogError(error.message || 'Could not update pickup point');
		} finally {
			setSaving(false);
		}
	};

	const openingDays = useMemo(
		() => {
			let hours = pickupPoint?.opening_hours;
			if (typeof hours === 'string') {
				try { hours = JSON.parse(hours); } catch (error) { return []; }
			}
			return Array.isArray(hours?.days) ? hours.days : [];
		},
		[pickupPoint]
	);
	const todayName = moment().format('dddd');
	const todayHours = useMemo(
		() => openingDays.find((d) => d.day === todayName),
		[openingDays, todayName]
	);

	if (!isPickupOrder && !pickupPoint) return null;

	const showMap = hasCoordinates(pickupPoint);
	const directionsUrl = showMap
		? `https://www.google.com/maps/search/?api=1&query=${pickupPoint.latitude},${pickupPoint.longitude}`
		: null;

	return (
		<Card className="mb-3">
			<CardHeader
				action={isPickupOrder && <Button color="primary" onClick={openDialog}
					disabled={hasUnsavedChanges} title={hasUnsavedChanges ? 'Save order changes first' : ''}>
					Change pickup point
				</Button>}
				title={
					<Box display="flex" alignItems="center" flexWrap="wrap">
						<Typography variant="h6"><b>PICKUP POINT</b></Typography>
						<Box ml={2} display="flex" alignItems="center">
							{pickupPoint && <Chip
								className={classes.chip}
								size="small"
								label={pickupPoint.is_active ? 'Active' : 'Inactive'}
								style={{
									backgroundColor: pickupPoint.is_active ? colors.primary : colors.darkLight,
									color: colors.white,
								}}
							/>}
							{pickupPoint?.has_cool_storage && (
								<Chip
									className={classes.chip}
									size="small"
									icon={<AcUnitIcon style={{fontSize: 14, color: colors.white}}/>}
									label="Cool storage"
									style={{backgroundColor: colors.blue, color: colors.white}}
								/>
							)}
						</Box>
					</Box>
				}
			/>
			<CardContent>
				<Table className={classes.table}>
					<TableBody>
						<TableRow>
							<TableCell className={classes.labelCell}>Name</TableCell>
							<TableCell>{pickupPoint?.name || 'No pickup point selected'}</TableCell>
						</TableRow>
						{isPickupOrder && <TableRow>
							<TableCell className={classes.labelCell}>Pickup contact phone</TableCell>
							<TableCell>
								<Box display="flex" alignItems="flex-start" flexWrap="wrap" style={{gap: 8}}>
									<TextField
										value={contactPhone}
										onChange={(event) => { setContactPhone(event.target.value); setPhoneError(''); }}
										type="tel"
										placeholder="Contact phone number"
										inputProps={{maxLength: 30, 'aria-label': 'Pickup contact phone number'}}
										error={Boolean(phoneError)}
										helperText={phoneError}
										disabled={savingPhone}
									/>
									<Button color="primary" variant="contained" onClick={saveContactPhone}
										disabled={savingPhone || hasUnsavedChanges || contactPhone.trim() === (details?.pickup_contact_phone || '')}>
										{savingPhone ? 'Saving...' : 'Save'}
									</Button>
								</Box>
							</TableCell>
						</TableRow>}
						<TableRow>
							<TableCell className={classes.labelCell}>Delivery company</TableCell>
							<TableCell>
								{deliveryCompany
									? `${deliveryCompany.name}${deliveryCompany.code ? ` (${deliveryCompany.code})` : ''}`
									: '-'}
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell className={classes.labelCell}>Address</TableCell>
							<TableCell>
								{pickupPoint?.address || '-'}
								{directionsUrl && (
									<a
										href={directionsUrl}
										target="_blank"
										rel="noopener noreferrer"
										className={classes.link}
									>
										Directions <LaunchIcon style={{fontSize: 14, marginLeft: 2}}/>
									</a>
								)}
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell className={classes.labelCell}>Phone</TableCell>
							<TableCell>
								{pickupPoint?.phone || '-'}
								{deliveryCompany?.contact_phone &&
									deliveryCompany.contact_phone !== pickupPoint?.phone && (
										<Typography variant="caption" style={{color: colors.darkRegular, display: 'block'}}>
											Company: {deliveryCompany.contact_phone}
										</Typography>
									)}
							</TableCell>
						</TableRow>
						{openingDays.length > 0 && (
							<TableRow>
								<TableCell className={classes.labelCell}>Opening hours</TableCell>
								<TableCell>
									<Box className={`${classes.hoursRow} ${classes.hoursToday}`}>
										<span>{todayHours ? `${todayHours.day} (today)` : `${todayName} (today)`}</span>
										<span>{todayHours ? `${todayHours.open} - ${todayHours.close}` : 'Closed'}</span>
									</Box>
									<Collapse in={showAllHours}>
										{openingDays
											.filter((d) => d.day !== todayName)
											.map((d) => (
												<Box key={d.day} className={classes.hoursRow}>
													<span>{d.day}</span>
													<span>{d.open && d.close ? `${d.open} - ${d.close}` : 'Closed'}</span>
												</Box>
											))}
									</Collapse>
									<Button
										color="primary"
										className={classes.hoursToggle}
										onClick={() => setShowAllHours((prev) => !prev)}
										endIcon={showAllHours
											? <ExpandLessIcon style={{fontSize: 16}}/>
											: <ExpandMoreIcon style={{fontSize: 16}}/>}
									>
										{showAllHours ? 'Hide full week' : 'Show full week'}
									</Button>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>

				{showMap ? (
					<Box className={classes.mapWrapper}>
						<PickupPointMap
							latitude={pickupPoint.latitude}
							longitude={pickupPoint.longitude}
							name={pickupPoint.name}
							address={pickupPoint.address}
						/>
					</Box>
				) : (
					<Box className={classes.noCoords}>
						<Typography variant="body2" style={{color: colors.darkRegular}}>
							No coordinates available for this pickup point.
						</Typography>
					</Box>
				)}
			</CardContent>
			<Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
				<DialogTitle>Change pickup point</DialogTitle>
				<DialogContent dividers>
					{loadingPoints ? <Box display="flex" justifyContent="center" p={2}><CircularProgress size={28}/></Box> : (
						<Box my={2}>
							<Typography variant="body2" gutterBottom>Pickup point</Typography>
							<SearchableSelect
								value={pointOptions.find((option) => Number(option.value) === Number(selectedId)) || null}
								onChange={(option) => setSelectedId(option ? option.value : '')}
								options={pointOptions}
								isSearchable
								isOptionDisabled={(option) => option.isDisabled}
								placeholder="Search by station name or address..."
								noOptionsMessage={() => 'No pickup points found'}
								menuPortalTarget={document.body}
								styles={{menuPortal: (base) => ({...base, zIndex: 1500})}}
							/>
						</Box>
					)}
					<Typography variant="caption" color="textSecondary">
						Changing the pickup point keeps the order's payment amount unchanged.
					</Typography>
					{dialogError && <Typography color="error" variant="body2">{dialogError}</Typography>}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
					<Button color="primary" variant="contained" onClick={savePoint}
						disabled={saving || loadingPoints || !selectedId || Number(selectedId) === Number(pickupPoint?.id)}>
						{saving ? 'Saving...' : 'Save pickup point'}
					</Button>
				</DialogActions>
			</Dialog>
		</Card>
	);
};

export default PickupPointCard;
